const COURSES_ROOT_ROUTE_PATTERN = /^courses$/
const COURSE_SEARCH_ROUTE_PATTERN = /^courses\/search$/
const COURSE_PRODUCT_ROUTE_PATTERN = /^courses\/products\/([^/?#]+)$/
const COURSE_POST_ROUTE_PATTERN = /^courses\/products\/([^/]+)\/posts\/([^/?#]+)$/
const COURSE_MODULE_LESSON_ROUTE_PATTERN = /^courses\/products\/([^/]+)\/modules\/([^/]+)\/lessons\/([^/?#]+)$/
const COURSE_PURCHASE_ROUTE_PATTERN = /^courses\/products\/([^/]+)\/purchase-course$/
const COURSE_CATEGORY_ROUTE_PATTERN = /^courses\/products\/([^/]+)\/categories\/([^/?#]+)$/

export const splitCourseRedirectUrl = (redirectionUrl: string) => {
  const normalizedUrl = redirectionUrl.replace(/^\/+/, '')
  const [pathname, queryString = ''] = normalizedUrl.split('?')

  return {
    pathname,
    queryString,
  }
}

export const appendCourseRedirectQueryString = (pathname: string, queryString: string) => {
  return queryString ? `${pathname}?${queryString}` : pathname
}

export interface CourseRedirectTarget {
  name: string
  params?: Record<string, string>
  query?: Record<string, string>
}

const parseQueryString = (queryString: string) => {
  return Object.fromEntries(new URLSearchParams(queryString).entries())
}

export type ParsedCourseRedirectUrl =
  | {
      type: 'direct'
      target: CourseRedirectTarget
      getDefaultTemplate: boolean
    }
  | {
      type: 'category'
      productId: string
      moduleId: string
      query: Record<string, string>
      fallbackTarget: CourseRedirectTarget
      getDefaultTemplate: boolean
    }

export function parseCourseRedirectUrl(redirectionUrl: string): ParsedCourseRedirectUrl {
  const { pathname, queryString } = splitCourseRedirectUrl(redirectionUrl)
  const query = parseQueryString(queryString)

  if (COURSES_ROOT_ROUTE_PATTERN.test(pathname)) {
    return {
      type: 'direct',
      target: { name: 'courses', query },
      getDefaultTemplate: true,
    }
  }

  if (COURSE_SEARCH_ROUTE_PATTERN.test(pathname)) {
    return {
      type: 'direct',
      target: { name: 'courses-search', query },
      getDefaultTemplate: true,
    }
  }

  const purchaseMatch = pathname.match(COURSE_PURCHASE_ROUTE_PATTERN)
  if (purchaseMatch) {
    const [, productId] = purchaseMatch

    return {
      type: 'direct',
      target: {
        name: 'courses-purchase-product',
        params: { productId },
        query,
      },
      getDefaultTemplate: true,
    }
  }

  const productMatch = pathname.match(COURSE_PRODUCT_ROUTE_PATTERN)
  if (productMatch) {
    const [, productId] = productMatch

    return {
      type: 'direct',
      target: {
        name: 'courses-products',
        params: { productId },
        query,
      },
      getDefaultTemplate: true,
    }
  }

  const postMatch = pathname.match(COURSE_POST_ROUTE_PATTERN)
  if (postMatch) {
    const [, productId] = postMatch

    return {
      type: 'direct',
      target: {
        name: 'courses-products',
        params: { productId },
        query,
      },
      getDefaultTemplate: true,
    }
  }

  const moduleLessonMatch = pathname.match(COURSE_MODULE_LESSON_ROUTE_PATTERN)
  if (moduleLessonMatch) {
    const [, productId, moduleId, lessonId] = moduleLessonMatch

    return {
      type: 'direct',
      target: {
        name: 'courses-products-modules-lessons',
        params: { productId, moduleId, lessonId },
        query,
      },
      getDefaultTemplate: true,
    }
  }

  const categoryMatch = pathname.match(COURSE_CATEGORY_ROUTE_PATTERN)

  if (!categoryMatch) {
    return {
      type: 'direct',
      target: {
        name: 'courses',
        query,
      },
      getDefaultTemplate: true,
    }
  }

  const [, productId, moduleId] = categoryMatch
  return {
    type: 'category',
    productId,
    moduleId,
    query,
    fallbackTarget: {
      name: 'courses-products',
      params: { productId },
      query,
    },
    getDefaultTemplate: true,
  }
}

export function buildCourseModuleLessonRedirectTarget(
  productId: string,
  moduleId: string,
  lessonId: string,
  query: Record<string, string>
): CourseRedirectTarget {
  return {
    name: 'courses-products-modules-lessons',
    params: { productId, moduleId, lessonId },
    query,
  }
}
